import React, { useContext } from 'react'
import { Entry, Locale } from 'contentful'
import kebabCase from 'lodash.kebabcase'
import buildPath from 'marketing-site/lib/buildPath'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import * as Utils from 'marketing-site/src/library/utils'

import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import CurrentLocaleContext from 'marketing-site/src/components/context/CurrentLocaleContext'
import {
  IHeaderNavigationSolution as IContentfulHeaderNavigationSolution,
  IHeaderNavigationSolutions as IContentfulHeaderNavigationSolutions,
  IHeaderNavigationMenuDescription as IContentfulHeaderNavigationMenuDescription,
  IHeaderNavigationMenuMvp as IContentfulHeaderNavigationMenuMVP,
  IHeaderNavigationMenuSection as IContentfulHeaderNavigationMenuSection,
  IHeaderNavigationSpotlight as IContentfulHeaderNavigationSpotlight,
  IHeaderNavigationLink as IContentfulHeaderNavigationLink,
  IHeaderNavigationMvp as IContentfulHeaderNavigationMVP,
  IHeaderNavigationMenuItem as IContentfulHeaderNavigationMenuItem,
} from 'marketing-site/@types/generated/contentful'
import {
  IHeaderNavigationSolution,
  IHeaderNavigationSolutions,
  IHeaderNavigationMenuDescription,
  IHeaderNavigationMenu as IHeaderNavigationMenu,
  IProps as IHeaderNavigation,
  IHeaderNavigationMenuSection,
  IHeaderNavigationMenuItem,
} from 'marketing-site/src/mvp/library/components/HeaderNavigation'
import { HeaderNavigation } from 'marketing-site/src/mvp/library/components/HeaderNavigation'
import { transformCTAWithBackground } from 'marketing-site/src/mvp/transformers/elements/ContentfulCTALink'
import { transformMutinyCtasContainer } from 'marketing-site/src/transformers/components/ContentfulMutinyCtasContainer'
import {
  transformHeaderNavigationSpotlight,
  transformHeaderNavigationLink,
  transformSigninLink as transformSignInLink,
  transformHeaderNavigationLinkBadge,
} from '../../../transformers/components/ContentfulHeaderNavigation'
import { transformSubNav } from 'marketing-site/src/mvp/transformers/components/ContentfulSubNav'

export function isHeaderNavigationMVP(entry: Entry<any>): entry is IContentfulHeaderNavigationMVP {
  return entry.sys.contentType?.sys.id === 'headerNavigationMVP'
}

export const ContentfulHeaderNavigation = (
  headerNavigation: IContentfulHeaderNavigationMVP,
): React.ReactElement => {
  const currentLocale = useContext(CurrentLocaleContext)
  return (
    <EntryMarker entry={headerNavigation}>
      {() => <HeaderNavigation {...transformHeaderNavigation(headerNavigation, currentLocale)} />}
    </EntryMarker>
  )
}

const transformHeaderNavigationSolution = ({
  fields,
}: IContentfulHeaderNavigationSolution): IHeaderNavigationSolution => {
  return {
    ...fields,
    cta: fields.cta && transformCTAWithBackground(fields.cta),
    secondCta: fields.secondCTA && transformCTAWithBackground(fields.secondCTA),
    additionalCTA: fields.additionalCTA && transformCTAWithBackground(fields.additionalCTA),
  }
}

const transformHeaderNavigationSolutions = ({
  fields,
}: IContentfulHeaderNavigationSolutions): IHeaderNavigationSolutions => {
  return {
    ...fields,
    solutions:
      fields.solutions &&
      fields.solutions.map((solution) => transformHeaderNavigationSolution(solution)),
  }
}

const transformHeaderNavigationMenuDescription = ({
  fields,
}: IContentfulHeaderNavigationMenuDescription): IHeaderNavigationMenuDescription => {
  return {
    ...fields,
    contentType: 'menuDescription',
    cta: fields.cta && transformCTAWithBackground(fields.cta),
  }
}

const transformHeaderNavigationMenuSection = (
  { fields }: IContentfulHeaderNavigationMenuSection,
  locale: Locale,
): IHeaderNavigationMenuSection => {
  return {
    ...fields,
    items: fields.items.map((item) => transformHeaderNavigationMenuItem(item, locale)),
    itemsSecondColumn:
      fields.itemsSecondColumn &&
      fields.itemsSecondColumn.map((item) => transformHeaderNavigationMenuItem(item, locale)),
  }
}

const transformIcon = (iconReference: IContentfulHeaderNavigationMenuItem['fields']['icon']) => {
  if (!iconReference) return undefined
  const [family, icon] = iconReference.toLowerCase().split(' - ')
  return {
    family: kebabCase(family),
    icon: kebabCase(icon),
  }
}

export const transformHeaderNavigationMenuItem = (
  { fields }: IContentfulHeaderNavigationMenuItem,
  locale: Locale,
): IHeaderNavigationMenuItem => {
  return {
    ...fields,
    url: localizeUrl(fields.url, locale),
    icon: fields.icon && (transformIcon(fields.icon) as IHeaderNavigationMenuItem['icon']),
    badge: fields.badge ? transformHeaderNavigationLinkBadge(fields.badge) : undefined,
    newWindow: fields.openInNewWindow,
    titleIcon: fields.titleIcon && Utils.getTypedIcon(fields.titleIcon),
    cta: fields.cta && transformCTAWithBackground(fields.cta),
    subhead: fields.subhead && documentToReactComponents(fields.subhead),
  }
}

const transformHeaderNavigationMenuContent = (
  fields:
    | IContentfulHeaderNavigationMenuDescription
    | IContentfulHeaderNavigationMenuSection
    | IContentfulHeaderNavigationSolutions,
  locale: Locale,
) => {
  switch (fields.sys.contentType.sys.id) {
    case 'headerNavigationMenuDescription':
      return transformHeaderNavigationMenuDescription(
        fields as IContentfulHeaderNavigationMenuDescription,
      )
    case 'headerNavigationMenuSection':
      return transformHeaderNavigationMenuSection(
        fields as IContentfulHeaderNavigationMenuSection,
        locale,
      )
    case 'headerNavigationSolutions':
      return transformHeaderNavigationSolutions(fields as IContentfulHeaderNavigationSolutions)
  }
}

const transformHeaderNavigationMenuSidebar = (
  fields: IContentfulHeaderNavigationSpotlight | IContentfulHeaderNavigationMenuSection,
  locale: Locale,
) => {
  switch (fields.sys.contentType.sys.id) {
    case 'headerNavigationSpotlight':
      return transformHeaderNavigationSpotlight(
        fields as IContentfulHeaderNavigationSpotlight,
        locale,
      )
    case 'headerNavigationMenuSection':
      return transformHeaderNavigationMenuSection(
        fields as IContentfulHeaderNavigationMenuSection,
        locale,
      )
  }
}

const transformHeaderNavigationMenu = (
  { fields }: IContentfulHeaderNavigationMenuMVP,
  locale: Locale,
): IHeaderNavigationMenu => {
  return {
    ...fields,
    content:
      fields.content &&
      fields.content.map((item) => transformHeaderNavigationMenuContent(item, locale)),
    sidebar:
      fields.sidebar &&
      fields.sidebar.map((item) => transformHeaderNavigationMenuSidebar(item, locale)),
  }
}

const transformHeaderNavigationMenuOrLink = (
  fields: IContentfulHeaderNavigationMenuMVP | IContentfulHeaderNavigationLink,
  locale: Locale,
) => {
  switch (fields.sys.contentType.sys.id) {
    case 'headerNavigationMenuMVP':
      return transformHeaderNavigationMenu(fields as IContentfulHeaderNavigationMenuMVP, locale)
    case 'headerNavigationLink':
      return transformHeaderNavigationLink(fields as IContentfulHeaderNavigationLink, locale)
  }
}

const transformHeaderNavigation = (
  { fields }: IContentfulHeaderNavigationMVP,
  locale: Locale,
): IHeaderNavigation => {
  return {
    ...fields,
    contentType:
      fields.contentType && (kebabCase(fields.contentType) as IHeaderNavigation['contentType']),
    theme: fields.theme && (kebabCase(fields.theme) as IHeaderNavigation['theme']),
    navigationElements: (fields.navigationElements || []).map((navigationElement) =>
      transformHeaderNavigationMenuOrLink(navigationElement, locale),
    ),
    signInLink: fields.signInLink && transformSignInLink(fields.signInLink, locale),
    mutinyCtaContainer:
      fields.mutinyCtasContainer && transformMutinyCtasContainer(fields.mutinyCtasContainer),
    subNavigation: fields.subNavigation && transformSubNav(fields.subNavigation),
  }
}

function localizeUrl(url: string, locale: Locale) {
  if (url.includes('://')) {
    return url
  } else {
    return buildPath({ localeCode: locale.code, pathname: url }).localized
  }
}
